/**
 * Fetches `/api/admin/config` once on mount and exposes the result to any
 * descendant. Drives:
 *   - The env banner shown across test.merkflow.eu
 *   - The Mock Platform nav entry gating on admin sidebar
 *   - The MockPlatformPage route-level redirect
 *
 * Unauthenticated read — the endpoint is whitelisted in SecurityConfig
 * because the banner must show even before login on test.
 *
 * Exposes `loading: true` until the first fetch resolves so consumers
 * don't act on the default `mockEnabled=false` before the real value
 * arrives. Without this, navigating into /admin/mock-platform
 * synchronously mounts a fresh provider under AdminLayout; the page's
 * redirect check reads the default `false` and bounces to /dashboard
 * before the fetch ever completes.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { adminConfigApi, type AdminConfig } from '@/pages/admin/mock/mockApi';

interface AdminConfigState extends AdminConfig {
  loading: boolean;
}

const AdminConfigContext = createContext<AdminConfigState>({ mockEnabled: false, loading: true });

export function AdminConfigProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdminConfigState>({ mockEnabled: false, loading: true });

  useEffect(() => {
    let cancelled = false;
    adminConfigApi
      .get()
      .then((c) => {
        if (!cancelled) setState({ ...c, loading: false });
      })
      .catch(() => {
        // Failed fetch = fall back to mockEnabled: false. Clear loading
        // so consumers don't hang forever — e.g. on prod where the
        // endpoint returns {mockEnabled:false} and the page correctly
        // redirects, OR when the backend is down and the banner/nav
        // should simply stay hidden.
        if (!cancelled) setState({ mockEnabled: false, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <AdminConfigContext.Provider value={state}>{children}</AdminConfigContext.Provider>;
}

export function useAdminConfig(): AdminConfigState {
  return useContext(AdminConfigContext);
}
